import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MODULES, LOGO_URL, ROLE_LABELS } from "../lib/constants";
import * as Icons from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import TimeClock, { AttendanceNotice } from "./TimeClock";
import { AttendanceProvider } from "../context/AttendanceContext";
import { MobilePushSetup } from "./MobilePushSetup";
import { NotificationsBell } from "./NotificationsBell";

function LayoutBody() {
  const { user, logout, hasModule, theme, setTheme, setUiPref } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  const canMessages = hasModule("messages");
  const refreshUnread = useCallback(() => {
    if (!canMessages) return;
    api.get("/messages/unread-count").then((r) => setUnreadMsgs(r.data?.count || 0)).catch(() => {});
  }, [canMessages]);
  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, 20000);
    const onRead = () => setUnreadMsgs(0);
    window.addEventListener("gsp:messages-read", onRead);
    window.addEventListener("focus", refreshUnread);
    return () => { clearInterval(timer); window.removeEventListener("gsp:messages-read", onRead); window.removeEventListener("focus", refreshUnread); };
  }, [refreshUnread]);
  const markMessagesRead = () => {
    setUnreadMsgs(0);
    api.post("/messages/mark-read").catch(() => {});
  };

  const visible = MODULES.filter((m) => hasModule(m.key));
  if (!visible.some((m) => m.key === "timesheets")) visible.push({ key: "timesheets", label: "My attendance", path: "/app/timesheets", icon: "Clock3" });
  visible.splice(Math.max(0, visible.findIndex((m) => m.key === "messages") + 1), 0, { key: "chat", label: "Team chat", path: "/app/chat", icon: "MessagesSquare" });
  if (hasModule("kanban")) visible.splice(visible.findIndex((m) => m.key === "kanban") + 1, 0, { key: "quote-expired", label: "Quote expired", path: "/app/quote-expired", icon: "CalendarX2" });
  const navCollapsed = !!user?.ui_prefs?.sidebar_collapsed;
  if (["super_admin", "admin", "admin_manager"].includes(user?.role)) visible.push({ key: "error-logs", label: "Error Logs", path: "/app/error-logs", icon: "TriangleAlert" });

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 ${navCollapsed ? "lg:w-16" : "lg:w-64"} bg-card border-r border-border flex flex-col transition-all ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
        <div className="p-5 border-b border-border flex items-center justify-between gap-2">
          <img src={LOGO_URL} alt="Grian Solar Power" className={`h-10 w-auto ${navCollapsed ? "lg:hidden" : ""}`} />
          <span className={navCollapsed ? "lg:hidden" : ""}><NotificationsBell /></span>
          <button data-testid="sidebar-collapse-toggle" title={navCollapsed ? "Expand menu" : "Collapse menu"}
            onClick={() => setUiPref("sidebar_collapsed", !navCollapsed)}
            className="hidden lg:inline-flex p-1.5 rounded-md hover:bg-muted text-muted-foreground shrink-0">
            {navCollapsed ? <Icons.PanelLeftOpen size={18} /> : <Icons.PanelLeftClose size={18} />}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {visible.map((m) => {
            const Icon = Icons[m.icon] || Icons.Circle;
            return (
              <NavLink key={m.key} to={m.path} end={m.key === "dashboard"}
                onClick={() => { setOpen(false); if (m.key === "messages") markMessagesRead(); }}
                data-testid={`nav-${m.key}`} title={m.label}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 relative ${navCollapsed ? "lg:justify-center lg:px-2" : ""} ${
                    isActive ? "bg-secondary text-secondary-foreground font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }>
                <Icon size={18} className="shrink-0" /> <span className={navCollapsed ? "flex-1 lg:hidden" : "flex-1"}>{m.label}</span>
                {m.key === "messages" && unreadMsgs > 0 && (
                  <span data-testid="messages-unread-badge" title={`${unreadMsgs} new message${unreadMsgs > 1 ? "s" : ""}`}
                    className={`min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold ${navCollapsed ? "lg:absolute lg:top-1 lg:right-1 lg:min-w-0 lg:h-2.5 lg:w-2.5 lg:px-0 lg:text-[0px]" : "ml-auto"}`}>
                    {unreadMsgs > 99 ? "99+" : unreadMsgs}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className={navCollapsed ? "lg:hidden" : ""}><TimeClock /></div>
          <div className="flex items-center justify-between gap-2">
            <div className={navCollapsed ? "lg:hidden" : ""}>
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="label-caps mb-3">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button data-testid="theme-toggle" title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`p-2 rounded-md border border-border hover:bg-muted transition-colors ${navCollapsed ? "lg:mx-auto" : ""}`}>
              {theme === "dark" ? <Icons.Sun size={16} /> : <Icons.Moon size={16} />}
            </button>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} title="Sign out"
            className={`w-full flex items-center gap-2 text-sm text-destructive hover:opacity-80 transition-all ${navCollapsed ? "lg:justify-center" : ""}`}>
            <Icons.LogOut size={16} /> <span className={navCollapsed ? "lg:hidden" : ""}>Sign out</span>
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 p-4 border-b border-border bg-card">
          <button onClick={() => setOpen(true)} data-testid="menu-btn"><Icons.Menu size={22} /></button>
          <img src={LOGO_URL} alt="logo" className="h-8" />
          <div className="ml-auto"><NotificationsBell align="right" /></div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <TimeClock compact />
          <AttendanceNotice />
          <MobilePushSetup />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function Layout() {
  return <AttendanceProvider><LayoutBody /></AttendanceProvider>;
}
